/**
 * Sync Service
 * Handles offline sync queue processing and background synchronization
 */

import {
  addToSyncQueue,
  getSyncQueueItems,
  removeFromSyncQueue,
  updateSyncQueueItem,
  type SyncQueueItem,
} from '@/lib/storage/indexeddb';
import { getSupabase } from '@/lib/db/supabase';

// Simple logger for sync service (client-safe)
const logger = {
  info: (message: string, context?: Record<string, unknown>) => {
    console.log(`[Sync] ${message}`, context || '');
  },
  warn: (message: string, context?: Record<string, unknown>) => {
    console.warn(`[Sync] ${message}`, context || '');
  },
  error: (message: string, error?: Error, context?: Record<string, unknown>) => {
    console.error(`[Sync] ${message}`, error, context || '');
  },
};

const MAX_RETRIES = 3;
const RETRY_DELAY = 5000; // 5 seconds

/**
 * Check if user is online
 */
export function isOnline(): boolean {
  return typeof navigator !== 'undefined' && navigator.onLine;
}

/**
 * Process a single sync queue item
 */
async function processSyncItem(item: SyncQueueItem): Promise<boolean> {
  try {
    const supabase = getSupabase();
    if (!supabase) {
      logger.warn('Supabase not configured - skipping sync item');
      return false;
    }

    const { type, action, data, userId } = item;

    switch (type) {
      case 'journal': {
        if (action === 'create') {
          const { error } = await supabase.from('journal_entries').insert({
            user_id: userId,
            ...data,
          });
          if (error) throw error;
        } else if (action === 'update') {
          const { id, ...updateData } = data as { id: string; [key: string]: unknown };
          const { error } = await supabase
            .from('journal_entries')
            .update(updateData)
            .eq('id', id)
            .eq('user_id', userId);
          if (error) throw error;
        } else if (action === 'delete') {
          const { id } = data as { id: string };
          const { error } = await supabase
            .from('journal_entries')
            .delete()
            .eq('id', id)
            .eq('user_id', userId);
          if (error) throw error;
        }
        break;
      }
      case 'ritual': {
        if (action === 'create' || action === 'update') {
          const { error } = await supabase.from('ritual_sessions').upsert({
            user_id: userId,
            ...data,
          });
          if (error) throw error;
        }
        break;
      }
      case 'habit': {
        if (action === 'create' || action === 'update') {
          const { error } = await supabase.from('habit_entries').upsert({
            user_id: userId,
            ...data,
          });
          if (error) throw error;
        } else if (action === 'delete') {
          const { id } = data as { id: string };
          const { error } = await supabase
            .from('habit_entries')
            .delete()
            .eq('id', id)
            .eq('user_id', userId);
          if (error) throw error;
        }
        break;
      }
      case 'completion': {
        if (action === 'create') {
          const { error } = await supabase.from('completions').insert({
            user_id: userId,
            ...data,
          });
          if (error) throw error;
        }
        break;
      }
      default:
        logger.warn('Unknown sync item type', { type });
        return false;
    }

    return true;
  } catch (error) {
    logger.error('Failed to process sync item', error as Error, { item });
    throw error;
  }
}

/**
 * Process all pending sync queue items
 */
export async function processSyncQueue(userId: string): Promise<{
  succeeded: number;
  failed: number;
  total: number;
}> {
  if (!isOnline()) {
    logger.info('Offline - skipping sync queue processing');
    return { succeeded: 0, failed: 0, total: 0 };
  }

  const items = await getSyncQueueItems(userId);
  let succeeded = 0;
  let failed = 0;

  logger.info('Processing sync queue', { count: items.length });

  for (const item of items) {
    if (!item.id) continue;

    try {
      const success = await processSyncItem(item);
      if (success) {
        await removeFromSyncQueue(item.id);
        succeeded++;
      } else {
        await updateSyncQueueItem(item.id, {
          retries: item.retries + 1,
          lastError: 'Unknown error',
        });
        failed++;
      }
    } catch (error) {
      const newRetries = item.retries + 1;
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';

      if (newRetries >= MAX_RETRIES) {
        // Max retries reached - mark as failed permanently
        logger.error('Sync item failed after max retries', error as Error, { item });
        await removeFromSyncQueue(item.id);
        failed++;
      } else {
        // Update retry count and error message
        await updateSyncQueueItem(item.id, {
          retries: newRetries,
          lastError: errorMessage,
        });
        failed++;
      }
    }

    // Small delay between items to avoid overwhelming the server
    await new Promise((resolve) => setTimeout(resolve, 100));
  }

  logger.info('Sync queue processing complete', {
    succeeded,
    failed,
    total: items.length,
  });

  return { succeeded, failed, total: items.length };
}

/**
 * Queue a sync action for later processing
 */
export async function queueSyncAction(
  type: SyncQueueItem['type'],
  action: SyncQueueItem['action'],
  data: Record<string, unknown>,
  userId: string
): Promise<number> {
  const id = await addToSyncQueue(type, action, data, userId);
  logger.info('Added item to sync queue', { id, type, action });

  // If online, try to process immediately
  if (isOnline()) {
    // Process in background (don't await)
    processSyncQueue(userId).catch((error) => {
      logger.error('Background sync failed', error);
    });
  }

  return id;
}

/**
 * Register background sync (if supported)
 */
export async function registerBackgroundSync(): Promise<void> {
  if (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'sync' in (ServiceWorkerRegistration.prototype as unknown as Record<string, unknown>)
  ) {
    try {
      const registration = await navigator.serviceWorker.ready;
      await (
        registration as unknown as { sync: { register: (tag: string) => Promise<void> } }
      ).sync.register('api-queue');
      logger.info('Background sync registered');
    } catch (error) {
      logger.error('Failed to register background sync', error as Error);
    }
  }
}

/**
 * Set up automatic sync on online event
 */
export function setupAutoSync(userId: string): () => void {
  const handleOnline = () => {
    logger.info('Connection restored - processing sync queue');
    processSyncQueue(userId).catch((error) => {
      logger.error('Auto-sync failed', error);
    });
  };

  window.addEventListener('online', handleOnline);

  // Return cleanup function
  return () => {
    window.removeEventListener('online', handleOnline);
  };
}

/**
 * Get sync queue status
 */
export async function getSyncQueueStatus(userId: string): Promise<{
  pending: number;
  failed: number;
  total: number;
}> {
  const items = await getSyncQueueItems(userId);
  const failed = items.filter((item) => item.retries >= MAX_RETRIES).length;

  return {
    pending: items.length - failed,
    failed,
    total: items.length,
  };
}
